import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import Divider from "@mui/material/Divider";
import { Deal } from "../../../validation/customer.validation";

interface Props {
  deals: Deal[];
  loading: boolean;
  handleInterestClick: (deal: Deal) => void;
  updateDealStatus?: (dealId: string, status: string) => void;
}

const BankerDealsCardView: React.FC<Props> = ({ deals, loading, handleInterestClick }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {loading ? (
        <Typography className="text-gray-500 text-center mt-6 col-span-full">
          Loading deals...
        </Typography>
      ) : deals.length === 0 ? (
        <Box className="flex flex-col items-center justify-center py-32 text-center text-gray-500 bg-white rounded-lg border border-gray-200 mx-4 col-span-full">
          <div className="flex items-center justify-center mb-4">
            <div className="animate-pulse">
              <SearchRoundedIcon
                className="text-gray-400"
                sx={{ fontSize: 48 }}
              />
            </div>
          </div>
          <Typography variant="h6" className="mt-4 text-gray-700">
            No Deals Found
          </Typography>
          <Typography variant="body2" className="text-gray-500">
            You can try adjusting your search or wait for new deals to be assigned.
          </Typography>
        </Box>
      ) : (
        deals.map((deal) => (
          <Card
            key={deal.id}
            className="rounded-2xl shadow-lg hover:shadow-2xl transition-transform transform hover:-translate-y-1 hover:scale-105 border-l-4 border-blue-500 bg-white"
          >
            {/* Card Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pt: 3, pb: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    {deal.loanName}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                <Button
                  variant="outlined"
                  color="info"
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
                  onClick={() => window.location.href = `/banker/deals/${deal.id}`}
                >
                  View Details
                </Button>
              </Box>
            </Box>
            <Divider />

            {/* Card Body */}
            <CardContent sx={{ px: 2, py: 1 }}>
              {/* Deal Details Section */}
              <Box sx={{ mb: 2, textAlign: 'left' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>
                  Deal Details
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    icon={<HomeWorkIcon />}
                    label={`Type: ${deal.dealType}`}
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    icon={<AttachMoneyIcon />}
                    label={`Size: $${parseInt(deal.loanSize.toString()).toLocaleString()}`}
                    color="secondary"
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    icon={<TrendingUpIcon />}
                    label={`LTV: ${deal.maxLTV}%`}
                    color="success"
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    icon={<AssessmentIcon />}
                    label={`DSC: ${deal.minDSCRatio}`}
                    color="warning"
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Box>

              {/* Customer Details Section */}
              {deal.isShareCustomerDetails && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>
                      Customer Details
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Name: {`${(deal?.customer as any)?.firstName || ''} ${(deal?.customer as any)?.middleName || ''} ${(deal?.customer as any)?.lastName || ''}`.trim() || "N/A"}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Email: {deal?.customer?.email || "N/A"}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Phone: {deal?.customer?.phone || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default BankerDealsCardView;
